// POS terminalının cihaz sessiyası — e-poçt/şifrəsiz giriş.
//
// KÖHNƏ AXIN (brauzer, hələ də qüvvədədir):
//   restoran e-poçtu + şifrə → device_token + işçi siyahısı → işçi seç → PIN
//
// POS TERMİNALINDA (Electron) BU AXIN YOXDUR. Səbəb: terminal restoranda
// açıqda durur və restoranın admin hesabının şifrəsi orada yazılmamalıdır.
// Əvəzinə cihazın kimliyi quraşdırma kodundadır — texniki işçi bir dəfə
// yapışdırır, tətbiq isə həmin tokenlə işləyir.
//
//   quraşdırma kodu → appToken → işçi siyahısı → işçi seç → PIN
//
// İŞÇİ SİYAHISININ İKİ MƏNBƏYİ VAR:
//   1. bulud `POST /auth/restaurant-verify` — aktual, rolları da gətirir
//   2. pos_local `GET /v1/auth/roster`      — sinxron olunmuş yerli nüsxə
//
// İkincisi OFLAYN üçündür: internet kəsiləndə POS işləməyə davam etməlidir,
// giriş ekranı isə hər şeyin qapısıdır. Bulud cavab verməsə yerli siyahı
// göstərilir və PIN yerli hash ilə yoxlanır (bax: authGateway.ts).

import { base_url } from '../index';
import { POS_APP_TOKEN, POS_CLOUD_API, POS_DEVICE_MANAGED, POS_ENABLED } from './config';
import { posRequest } from './posClient';

/**
 * Cihazın qoşulduğu buludun API kökü.
 *
 * Quraşdırma kodu hansı ünvanı göstəribsə O işlədilir; yalnız brauzer/dev
 * rejimində mənbə kodundakı sabit `base_url`-a qayıdılır.
 */
export function cloudApi(): string {
  return POS_CLOUD_API || base_url;
}

export interface StaffEntry {
  id: number | string;
  name: string;
  role?: string;
}

export interface RestaurantInfo {
  id?: number | string;
  name?: string;
  logo?: string | null;
}

export interface DeviceSession {
  staff: StaffEntry[];
  restaurant: RestaurantInfo | null;
  /** Siyahı buluddan gəldi (false = yerli nüsxə, oflayn rejim). */
  online: boolean;
}

/**
 * Quraşdırılmış POS terminalı: kimliyi admin panel təyin edir.
 *
 * Bu, giriş EKRANININ formasını müəyyən edir — e-poçt/şifrə addımı olmur.
 * `appToken`-in olub-olmaması ayrı sualdır (bax: `hasAppToken`).
 */
export function isDeviceManaged(): boolean {
  return POS_ENABLED && POS_DEVICE_MANAGED;
}

/** Buluda müraciət edə biləcək giriş tokeni varmı. */
export function hasAppToken(): boolean {
  return POS_APP_TOKEN !== '';
}

/**
 * Cihaz tokenini localStorage-a köçür.
 *
 * NİYƏ localStorage: mövcud kod (authGateway, PermissionsContext, shiftGateway)
 * `device_token`-i oradan oxuyur. Tokeni yeni yerə köçürmək həmin yerlərin
 * hamısını dəyişmək demək olardı; burada mənbə dəyişir, saxlanma yeri yox.
 */
export function bootstrapDeviceToken(): string {
  if (!hasAppToken()) return localStorage.getItem('device_token') || '';
  // Cihaz yenidən qeydiyyatdan keçəndə token dəyişir; localStorage-dakı
  // köhnəsi üstünlük qazanmamalıdır, yoxsa yeni quraşdırma işə düşməzdi.
  if (localStorage.getItem('device_token') !== POS_APP_TOKEN) {
    localStorage.setItem('device_token', POS_APP_TOKEN);
  }
  return POS_APP_TOKEN;
}

/**
 * Bulud cihazı tanımadı → Electron-a xəbər ver.
 *
 * Main process konfiqurasiyanı silir və quraşdırma ekranını açır. Burada
 * yalnız siqnal verilir; qərar main process-dədir, çünki fayl icazələri və
 * pəncərə idarəsi yalnız orada var.
 */
export async function reportDeviceRevoked(reason: string): Promise<void> {
  clearDeviceSession();
  try {
    await window.rezmoDevice?.reportRevoked(reason);
  } catch {
    /* IPC yoxdursa (brauzer) sadəcə yerli təmizləmə qalır */
  }
}

/** Cihazla bağlı bütün yerli izləri sil (ləğv və ya yenidən qurma zamanı). */
export function clearDeviceSession(): void {
  for (const key of [
    'device_token',
    'staff_list',
    'restaurant_info',
    'token',
    'role',
    'staff_role',
    'staff_permissions',
  ]) {
    localStorage.removeItem(key);
  }
}

/**
 * pos_local-un sinxron olunmuş işçi siyahısı (oflayn mənbə).
 *
 * EXPORT olunub: menecer təsdiqi dialoqu da bu siyahıdan istifadə edir —
 * təsdiq oflayn da işləməlidir, ona görə bulud siyahısına güvənə bilmərik.
 */
export async function fetchStaffRoster(): Promise<StaffEntry[]> {
  return localRoster();
}

async function localRoster(): Promise<StaffEntry[]> {
  const res = await posRequest<Array<{ id: string; displayName: string; role: string }>>({
    method: 'GET',
    path: '/v1/auth/roster',
    token: null,
  });
  if (!res.ok || !Array.isArray(res.data)) return [];
  // pos_local id-ləri `u_<cloudId>` formatındadır. Bulud PIN yoxlaması saf
  // rəqəm gözlədiyi üçün prefiksi çıxarırıq — beləliklə oflayn seçilən işçi
  // internet qayıdanda eyni cloud id ilə tanınır.
  return res.data.map((u) => ({
    id: /^u_\d+$/.test(u.id) ? Number(u.id.slice(2)) : u.id,
    name: u.displayName,
    role: u.role,
  }));
}

/**
 * Giriş ekranı üçün işçi siyahısını gətir.
 *
 * Bulud 401 qaytarsa bu, "token ləğv olunub" deməkdir və `onRevoked` çağırılır
 * — şəbəkə xətasından FƏRQLƏNDİRİLİR, çünki davranış tam başqadır: biri
 * müvəqqətidir və oflayn davam etmək olar, digəri qalıcıdır və cihaz yenidən
 * qeydiyyatdan keçməlidir.
 */
export async function loadDeviceSession(deviceToken: string): Promise<DeviceSession> {
  const token = deviceToken || localStorage.getItem('device_token') || '';

  if (token) {
    try {
      const res = await fetch(`${cloudApi()}/auth/restaurant-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ device_token: token }),
      });

      if (res.status === 401) {
        throw new DeviceRevokedError('Cihaz tokeni bulud tərəfindən qəbul edilmədi');
      }

      if (res.ok) {
        const json = (await res.json()) as { staff?: StaffEntry[]; restaurant?: RestaurantInfo };
        const staff = Array.isArray(json.staff) ? json.staff : [];
        localStorage.setItem('staff_list', JSON.stringify(staff));
        if (json.restaurant) localStorage.setItem('restaurant_info', JSON.stringify(json.restaurant));
        return { staff, restaurant: json.restaurant ?? null, online: true };
      }
      // 403 (restoran deaktiv/abunə bitib) və digər hallar: oflayn siyahıya keç.
      // Cihazı ləğv etmirik — problem restoran səviyyəsindədir və keçicidir.
    } catch (e) {
      if (e instanceof DeviceRevokedError) throw e;
      // Şəbəkə xətası — aşağıdakı oflayn mənbəyə düşürük.
    }
  }

  // ── Oflayn ────────────────────────────────────────────────────────────────
  const cached = readCachedStaff();
  const local = await localRoster().catch(() => [] as StaffEntry[]);
  // Yerli siyahı sinxronla yenilənir, ona görə keşdən üstündür; boşdursa
  // (məs. sinxron hələ bitməyib) son məlum keş göstərilir.
  const staff = local.length > 0 ? local : cached;
  if (staff.length > 0) localStorage.setItem('staff_list', JSON.stringify(staff));
  return { staff, restaurant: readCachedRestaurant(), online: false };
}

export class DeviceRevokedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DeviceRevokedError';
  }
}

function readCachedStaff(): StaffEntry[] {
  try {
    const raw = localStorage.getItem('staff_list');
    const list = raw ? (JSON.parse(raw) as StaffEntry[]) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function readCachedRestaurant(): RestaurantInfo | null {
  try {
    const raw = localStorage.getItem('restaurant_info');
    return raw ? (JSON.parse(raw) as RestaurantInfo) : null;
  } catch {
    return null;
  }
}
